<template>
  <a
    :href="this.data.link.url"
    :target="this.data.link.target"
    class="owc-nba-item"
    :class="{
      'owc-nba-item--dark': darkMode,
      'owc-nba-item--text-only-mode': isTextOnly,
    }"
  >
    <wb-icon class="owc-nba-item__icon" :name="this.data.icon" v-if="!isTextOnly"></wb-icon>
    <span class="owc-nba-item__content">
      <span class="owc-nba-item__title" v-html="this.data.label"></span>
      <span class="owc-nba-item__description" v-html="this.data.description"></span>
    </span>

    <wb-icon class="owc-nba-item__arrow" name="arrow-right"></wb-icon>
  </a>
</template>

<script lang="ts">
import Vue from 'vue';

/**
 * OWC NBA Item - this is a subcomponent for OWC Next Best Activities
 *
 * @vue-prop {Object} data - the content object
 * @vue-prop {Boolean} [darkMode=fase] - Dark Mode flag
 */
export default Vue.extend({
  props: {
    data: {
      type: Object,
      required: true,
    },
    darkMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isTextOnly: function(): boolean {
      return !this.data.icon;
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rtl-utils';

.owc-nba-item {
  $this: &;
  // redundant variables below because of context usage
  // also, this future proofs it
  $smallSpacing: 8px;
  $basePadding: 16px;
  $leftPadding: $basePadding;
  $iconSize: 24px;
  $iconSpacing: $basePadding;
  $arrowSize: $basePadding;
  $arrowSpacing: $arrowSize;
  //text only overrides
  $leftPaddingTextOnly: 24px;
  $rightPaddingTextOnly: 40px;
  // transitions:
  // arrow
  $arrowInTransition: transform 0.4s $wb-move 0.15s, opacity 0.4s $wb-fade 0.15s;
  $arrowOutTransition: transform 0.4s $wb-move 0s, opacity 0.4s $wb-fade 0s;
  // icon
  $iconInTransition: transform 0.3s $wb-move 0.1s, opacity 0.3s $wb-fade 0.1s;
  $iconOutTransition: transform 0.3s $wb-move 0.15s, opacity 0.3s $wb-fade 0.15s;
  // content
  $contentInTransition: transform 0.5s $wb-move 0.1s;
  $contentOutTransition: transform 0.5s $wb-move;
  // displacements
  // STYLELINT is disabled in the next rules since we want the operator-no-unspaced rule in general but not when it applies to the cases of "-1"
  // stylelint-disable-next-line scss/operator-no-unspaced
  --contentTransformX: #{$iconSize * -1};
  // stylelint-disable-next-line scss/operator-no-unspaced
  --imageryTransformX: #{$smallSpacing * -1};

  @include wb-type-copy-secondary;

  position: relative;

  display: flex;
  width: 100%;
  height: 100%;
  padding: var(--wb-spacing-xs) #{$basePadding};
  border: 1px solid var(--wb-grey-70);
  border-radius: 2px;

  color: var(--wb-grey-45);
  text-decoration: none;

  background-color: transparent;

  transition: background-color 0.4s linear;

  &__icon {
    // display flex is here to visually align
    // svgs within the given bounding box
    // ------------------------------
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    // ------------------------------
    box-sizing: content-box;
    width: $iconSize;
    height: $iconSize;
    padding-right: #{$basePadding};

    transition: $iconOutTransition;
  }
  &__content {
    display: block;

    transition: $contentOutTransition;
  }
  &__title,
  &__description {
    display: block;
  }
  &__icon,
  &__title {
    color: var(--wb-grey-20);
  }
  &__title {
    @include wb-type-copy-strong;
  }
  &__arrow {
    position: absolute;
    top: 50%;
    right: $arrowSpacing;

    display: block;
    width: $arrowSize;
    height: $arrowSize;

    color: var(--wb-blue-45);

    transform: translate(var(--imageryTransformX), -50%);
    opacity: 0;

    transition: $arrowOutTransition;
  }
  &--text-only-mode {
    padding-right: #{$rightPaddingTextOnly};
    padding-left: #{$leftPaddingTextOnly};
  }
  &--dark {
    border: 1px solid var(--wb-grey-40);

    color: var(--wb-grey-60);

    #{$this}__icon,
    #{$this}__title {
      color: var(--wb-white);
    }
  }
  &:hover,
  &:focus {
    background-color: var(--wb-grey-90);

    #{$this}__content {
      transform: translateX(var(--contentTransformX));

      transition: $contentInTransition;
    }
    #{$this}__icon {
      transform: translateX(var(--imageryTransformX));
      opacity: 0;

      transition: $iconInTransition;
    }
    #{$this}__arrow {
      --imageryTransformX: 0;
      opacity: 1;

      transition: $arrowInTransition;
    }
    &#{$this}--dark {
      background-color: var(--wb-grey-20);
    }
    &#{$this}--text-only-mode {
      #{$this}__content {
        transform: translateX(0);
      }
    }
  }

  @include rtl-rule('.owc-nba-item', false) {
    --contentTransformX: #{$iconSize * 1};
    --imageryTransformX: #{$smallSpacing * 1};

    &__icon {
      padding-right: initial;
      padding-left: #{$basePadding};
    }
    &__arrow {
      right: initial;
      left: $arrowSpacing;
    }
  }
}
</style>
